<template>
  <main class="bg-chatbot">
    <div class="page-space-25"></div>
    <section class="container-lil flex flex-col">
      
      <!-- Tags de réclamation rapides -->
      <div class="mb-5">
        <div 
          v-for="(tag, index) in quickTags" 
          :key="index"
          class="widget-chatbot-tag mb-3"
          @click="startNewChat(tag)"
          style="cursor: pointer;"
        >
          {{ tag.label }}
        </div>
      </div>

      <h1 class="secondary-font mt-8">Vos conversations</h1>
      
      <!-- Loading -->
      <template v-if="loading">
        <p class="text-center my-5">Chargement des conversations...</p>
      </template>

      <!-- Liste des conversations -->
      <template v-else>
        <router-link
          v-for="conversation in formattedConversations"
          :key="conversation.id"
          :to="`/front/aide/live-tchat/${conversation.id}`"
        >
          <div class="card-chatbot">
            <div class="s2">
              <img :src="conversation.agentAvatar" width="100%" class="rounded-100" alt="Agent" />
            </div>
            <div class="s18 px-7">
              <h4 class="flex align-items-center text-grey-800 mb-2 gap-4">
                {{ conversation.agentName }}
                <div 
                  v-if="conversation.tag" 
                  class="widget-chatbot-tag lil"
                >
                  {{ conversation.tag }}
                </div>
                <span 
                  v-if="conversation.status"
                  :class="['tag-state', conversation.statusClass, 'stroke']"
                >
                  {{ conversation.status }}
                </span>
              </h4>
              <p class="text-grey-600 mb-1">
                {{ conversation.lastMessage }}
              </p>
              <p class="secondary-font font-semibold text-sm text-grey-400">
                {{ formatTimeAgo(conversation.lastMessageTime) }}
              </p>
            </div>
          </div>
        </router-link>

        <!-- Message si aucune conversation -->
        <div v-if="formattedConversations.length === 0" class="text-center my-5">
          <p>Aucune conversation pour le moment.</p>
          <p class="text-grey-600 mt-2">Utilisez les tags ci-dessus pour démarrer une nouvelle conversation.</p>
        </div>
      </template>

      <div class="clear-both h-70"></div>
    </section>
  </main>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import chatService, { type ChatConversation } from '@/services/chatService'
import { CHAT_AGENTS } from '@/constants/chatAgents'

export default defineComponent({
  name: 'LiveTchatsView',
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const conversations = ref<ChatConversation[]>([])

    // Tags rapides pour démarrer une conversation (mapping vers les types du backend)
    const quickTags = ref([
      { label: 'Faire une réclamation', type: 'reclamation' },
      { label: 'Remboursement', type: 'remboursement' },
      { label: 'Signaler un problème sur mon compte', type: 'compte' }
    ])

    /**
     * Démarrer une nouvelle conversation avec un tag
     */
    const startNewChat = (tag: { label: string, type: string }) => {
      router.push({
        path: '/front/aide/live-tchat',
        query: { type: tag.type, tag: tag.label }
      })
    }

    /**
     * Formater le temps écoulé depuis le dernier message
     */
    const formatTimeAgo = (dateString: string): string => {
      if (!dateString) return ''
      
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) {
        return 'À l\'instant'
      } else if (diffMins < 60) {
        return `Il y a ${diffMins} minute${diffMins > 1 ? 's' : ''}`
      } else if (diffHours < 24) {
        return `Il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`
      } else if (diffDays < 7) {
        return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`
      } else {
        return date.toLocaleDateString('fr-FR')
      }
    }

    /**
     * Mapper le statut du backend vers les classes CSS
     */
    const getStatusClass = (statut: string): string => {
      if (statut === 'open' || statut === 'en cours') return 'warning'
      if (statut === 'termine' || statut === 'closed') return 'notif'
      return 'notif'
    }

    /**
     * Mapper le statut du backend vers le texte affiché
     */
    const getStatusLabel = (statut: string): string => {
      if (statut === 'open') return 'En cours'
      if (statut === 'termine' || statut === 'closed') return 'Traité'
      return statut
    }

    /**
     * Charger les conversations depuis l'API
     */
    const loadConversations = async () => {
      loading.value = true
      try {
        const response = await chatService.getConversations()
        if (response.data && Array.isArray(response.data)) {
          conversations.value = response.data
        }
      } catch (error) {
        console.error('Erreur lors du chargement des conversations:', error)
      } finally {
        loading.value = false
      }
    }

    // Formater les conversations pour l'affichage
    const formattedConversations = computed(() => {
      return conversations.value.map((conv: ChatConversation) => ({
        id: conv.id,
        agentName: conv.agentNom || CHAT_AGENTS[conv.id % CHAT_AGENTS.length].name,
        agentAvatar: conv.agentAvatar || CHAT_AGENTS[conv.id % CHAT_AGENTS.length].avatar,
        tag: conv.type,
        status: getStatusLabel(conv.statut),
        statusClass: getStatusClass(conv.statut),
        lastMessage: conv.dernierMessage?.texte || 'Aucun message',
        lastMessageTime: conv.dernierMessage?.date || new Date().toISOString()
      }))
    })

    onMounted(() => {
      loadConversations()
    })

    return {
      loading,
      conversations,
      formattedConversations,
      quickTags,
      startNewChat,
      formatTimeAgo
    }
  }
})
</script>

<style scoped>
/* Les styles globaux sont déjà définis dans les fichiers CSS globaux */
</style>
